import { useEffect, useMemo } from 'react';
import { TaskStatus } from '@/types/tasks.ts';
import { signalDropLanded } from '@/utils/upload/uploadDropSignal.ts';
import { getUploadSessionMemory } from '@/hooks/house/documents/uploadSessionMemory.ts';
import { formatHashPassSummary, formatSlowFileHash, SLOW_FILE_HASH_MS, startHashPass } from '@/utils/fileHash.ts';
import { makeLogger } from '@/utils/logger/logger.ts';
import useUploadedRecords from '../useUploadedRecords.ts';
import { House } from '@/client';
import { createTaskMetadata, generateUiTaskId, getDisplayName } from '@/utils/uploadUtils.ts';
import {
	excludeFilesAlreadyAnsweredFor,
	excludeSystemMetadataFiles,
	fileIdentity,
} from '@/utils/upload/uploadUtils.ts';
import { useHouseTaskManager } from '@/context/house/tasks/HouseTaskManagerContext.tsx';
import { isStatusWorthRetrying } from '@/context/house/tasks/helpers.ts';
import { getUITaskStatus } from '@/context/house/tasks/uiTask.ts';
import { useUploadDocument } from '@/hooks/house/documents/mutations/useUploadDocument.ts';
import { RecordTag } from '@/constants/documentTags.ts';
import {
	BatchOptions,
	BatchProgress,
	CONCURRENT_TASK_TYPES,
	TaskDefinition,
	TaskNotification,
} from '@/context/house/tasks/types.ts';

// Legacy interface for backward compatibility
export interface TaskExecutionOptions {
	tags?: string[];
	onComplete?: () => Promise<void>;
	onAccepted?: () => Promise<void>;
	showNotification?: (notification: TaskNotification) => void;
	onBatchCompleted?: () => Promise<void>;
	onBatchFailed?: () => Promise<void>;
	/**
	 * Optional progress callback from the UI; used to refetch documents as batches progress
	 */
	onBatchProgress?: (progress: BatchProgress) => Promise<void>;
	/** Whether uploaded documents should be private (visible only to admins) */
	isPrivate?: boolean;
}

const logger = makeLogger('useHouseRecordUpload');

/**
 * Hook containing business logic for document operations using the new centralized task manager
 *
 * @param house - The house objects to use for document operations
 */
export const useHouseRecordUpload = (house: House) => {
	const { uploadItems } = useUploadedRecords(house.id);
	const { mutateAsync: uploadSingleFile } = useUploadDocument();

	// Use the new centralized task manager with destructuring for proper React reactivity
	const { uiTasks, getStatus, scheduleTaskBatch, updateApiTaskMetadata } = useHouseTaskManager();

	// Everything the client already knows about this house's files, held per house rather than per
	// mount: the composer appears at several call sites, and each one building its own memory meant
	// a drop after navigating re-read every byte and recognised none of its own rows.
	const sessionMemory = getUploadSessionMemory(house.id);

	// A stable reference, so the session's observation short-circuits: this hook rerenders on every
	// upload progress tick.
	const documents = useMemo(() => uploadItems.map((item) => item.document), [uploadItems]);
	sessionMemory.observeDocumentList(documents);

	useEffect(() => {
		for (const { documentId, sentHash, storedHash } of sessionMemory.confirmUploads(documents)) {
			logger.error(
				`Content-hash mismatch for document ${documentId}: client and server hashing disagree — the duplicate gate is unreliable`,
				{ sentHash, storedHash },
			);
		}
	}, [documents, sessionMemory]);

	/**
	 * Creates a task definition for file upload. The task is scheduled before its bytes have been
	 * read, so the queue is on screen while hashing is still running; executeFn waits on this one
	 * file's hash, which by its turn has usually already landed.
	 *
	 * Claiming here rather than earlier is what catches content that arrived after scheduling — an
	 * in-flight earlier batch, or a list refetch — and it is never leaked by a task cancelled while
	 * still queued, since its executeFn simply never runs.
	 */
	const _createFileTaskDefinition = (
		file: File,
		hashPromise: Promise<string>,
		options?: TaskExecutionOptions,
	): TaskDefinition => {
		// Carried so the queue can give this file one row however many times it is dropped.
		const metadata = {
			...createTaskMetadata(house.id, file.size, file.name, options?.tags as RecordTag[]),
			fileIdentity: fileIdentity(file),
		};
		const taskId = generateUiTaskId(file);

		return {
			id: taskId,
			taskType: CONCURRENT_TASK_TYPES.UPLOAD_RECORD,
			metadata,
			executeFn: async (signal, reportProgress) => {
				const hash = await hashPromise;
				if (!sessionMemory.contentGate.claim(hash, taskId)) {
					// Only a match against the user's own visible records can be named. Content
					// merely in flight has no stored record yet, and a server-detected skip
					// withholds the document deliberately — its lookup is not visibility-filtered.
					const matched = sessionMemory.matchedDocument(hash);
					logger.info(`Skipping "${file.name}": content is already in this Bundle or on its way`, { hash });
					if (matched) {
						// Separate from `documentId`, which means "this task created it": a skip created
						// nothing, it recognised something. Deleting that record makes the file uploadable
						// again, which needs the id to notice.
						updateApiTaskMetadata(taskId, {
							matchedDocumentId: matched.id,
							...(matched.publicId ? { recordPublicId: matched.publicId } : {}),
						});
					}
					return null;
				}
				try {
					const documentId = await uploadSingleFile({
						houseId: house.id,
						file,
						tags: options?.tags as RecordTag[],
						private: options?.isPrivate ?? false,
						signal,
						onTransferProgress: reportProgress ? (fraction) => reportProgress(fraction * 100) : undefined,
						onRecordCreated: (recordPublicId) => updateApiTaskMetadata(taskId, { recordPublicId }),
					});
					if (documentId !== null) {
						// Awaiting the list, which retires the claim and checks the stored hash.
						sessionMemory.rememberSentHash(documentId, hash);
					} else {
						// The server skipped it: the Bundle already holds these bytes under a document
						// this upload did not create, so no list entry will ever arrive to retire the
						// claim. Left held it would outlive that document, and deleting it would not
						// make the content uploadable again. Worst case is one request the server
						// skips a second time.
						sessionMemory.contentGate.retire(hash);
					}
					return documentId;
				} catch (error) {
					// The bytes never landed, so the content must stay uploadable.
					sessionMemory.contentGate.release(taskId);
					throw error;
				}
			},
			onComplete: options?.onComplete, // Optional completion handler
			onAccepted: options?.onAccepted, // Optional handler when task is accepted
		};
	};

	/**
	 * Creates a task definition for text upload
	 */
	const _createTextTaskDefinition = (text: string, options?: TaskExecutionOptions): TaskDefinition => {
		const tagsArray = options?.tags ?? [];
		const firstTag = tagsArray[0];
		const displayName = getDisplayName(firstTag, text, tagsArray as RecordTag[]);
		const metadata = createTaskMetadata(house.id, text.length, displayName, tagsArray as RecordTag[]);

		return {
			id: generateUiTaskId(text),
			taskType: CONCURRENT_TASK_TYPES.UPLOAD_RECORD,
			metadata,
			executeFn: (signal) =>
				uploadSingleFile({
					houseId: house.id,
					text,
					tags: tagsArray as RecordTag[],
					private: options?.isPrivate ?? false,
					signal,
				}),
			onComplete: options?.onComplete, // Optional completion handler
			onAccepted: options?.onAccepted, // Optional handler when task is accepted
		};
	};

	/**
	 * The document this task's answer rests on: the one it created, or — for a skip — the one it
	 * recognised. Either way, deleting that document is what turns the answer back into work.
	 *
	 * A server-detected skip names no document, deliberately, so it has none and stays excluded.
	 */
	const _documentBehindOutcome = (task: (typeof uiTasks)[number]): number | null => {
		const metadata = task.execution?.apiTask?.metadata as Record<string, unknown> | undefined;
		const created = metadata?.documentId;
		if (typeof created === 'number') return created;
		const matched = metadata?.matchedDocumentId;
		return typeof matched === 'number' ? matched : null;
	};

	/**
	 * Whether dropping this task's file again would achieve anything.
	 *
	 * Work that never landed, and work whose document has since been deleted — re-dropping the
	 * folder is how someone restores a record they removed by mistake.
	 */
	const _needsAnotherAttempt = (task: (typeof uiTasks)[number]): boolean => {
		const status = getUITaskStatus(task);
		if (isStatusWorthRetrying(status)) return true;
		if (status !== TaskStatus.Completed && status !== TaskStatus.Skipped) return false;

		const documentId = _documentBehindOutcome(task);
		if (documentId === null) return false;
		return sessionMemory.hasBeenDeleted(documentId);
	};

	/**
	 * What of a drop is actually worth queueing. Both exclusions are about not reading bytes: on a
	 * cloud-synced folder every read is a network fetch, so a folder's invisible bookkeeping files
	 * and a re-drop of work already in progress are expensive, not free.
	 */
	const _selectFilesToSchedule = (droppedFiles: File[]): { carried: File[]; toSchedule: File[] } => {
		const realFiles = excludeSystemMetadataFiles(droppedFiles);
		const excludedCount = droppedFiles.length - realFiles.length;
		if (excludedCount > 0) {
			logger.info(`Ignoring ${excludedCount} system metadata file${excludedCount === 1 ? '' : 's'} in this drop`);
		}

		// A task the queue no longer holds is a missing answer, not a settled one: `taskIdByFile` lives
		// in session memory and the tasks are React state, so an id can outlive what it points at.
		// Hence a lookup rather than membership of a set, where an absent id reads as "not worth
		// doing again".
		const tasksById = new Map(uiTasks.map((task) => [task.id, task]));
		const filesToSchedule = excludeFilesAlreadyAnsweredFor(realFiles, sessionMemory.taskIdByFile, (taskId) => {
			const task = tasksById.get(taskId);
			return task === undefined || _needsAnotherAttempt(task);
		});
		const alreadyAnsweredCount = realFiles.length - filesToSchedule.length;
		if (alreadyAnsweredCount > 0) {
			logger.info(`${alreadyAnsweredCount} of this drop's files already have a row reporting them; keeping it`);
		}

		// A file the gate will skip still gets a task when this session holds no answer for it: content
		// stored by an earlier session is only discoverable by hashing, and a task settling as skipped
		// is how its row says so. A file this session has already answered for is excluded above.
		// Fewer identities than files means the folder holds the same file more than once. Those
		// copies share a row and are read once; the gap between the two counts is what the header owes
		// the user an explanation for.
		const distinctIdentities = new Set(filesToSchedule.map(fileIdentity)).size;
		logger.info(
			`Drop: ${droppedFiles.length} received · ${realFiles.length} after metadata files · ` +
				`${filesToSchedule.length} to schedule · ${distinctIdentities} distinct`,
		);

		// `carried` is what the user actually dragged, which is the number the queue owes them an
		// answer about — `toSchedule` is only what is left to do about it.
		return { carried: realFiles, toSchedule: filesToSchedule };
	};

	/**
	 * One hash promise per file, in drop order. Files hashed earlier in this session resolve without
	 * being read again — on a cloud-synced folder that read is a network fetch, and a re-drop asks
	 * for the same bytes the client has already seen.
	 *
	 * Nothing waits on the pass, so the queue renders while the reads run. Throughput is the number
	 * worth reading in the log: far below a local disk's hundreds of MB/s means the bytes were not
	 * local.
	 */
	const _resolveHashes = (filesToHash: File[]): Promise<string>[] => {
		const unreadFiles = filesToHash.filter((file) => !sessionMemory.hashByFile.has(fileIdentity(file)));
		if (unreadFiles.length < filesToHash.length) {
			logger.info(`Reusing ${filesToHash.length - unreadFiles.length} hash(es) already computed this session`);
		}

		// One read per distinct file, not per copy of it. A folder can hold the same file at two depths,
		// and on a cloud-synced volume each read is a full materialisation round trip — so the second
		// copy would spend one confirming a hash the first already produced.
		const firstCopyOfEach = new Map<string, File>();
		for (const file of unreadFiles) {
			const identity = fileIdentity(file);
			if (!firstCopyOfEach.has(identity)) firstCopyOfEach.set(identity, file);
		}
		const filesToRead = Array.from(firstCopyOfEach.values());
		const repeatCount = unreadFiles.length - filesToRead.length;
		if (repeatCount > 0) {
			logger.info(`Reading ${filesToRead.length} file(s); ${repeatCount} more are copies of one of them`);
		}

		const startedAt = performance.now();
		let hashedBytes = 0;
		const pass = startHashPass(filesToRead, ({ file, hash, elapsedMs }) => {
			sessionMemory.hashByFile.set(fileIdentity(file), hash);
			hashedBytes += file.size;
			if (elapsedMs >= SLOW_FILE_HASH_MS) logger.warn(formatSlowFileHash(file.name, file.size, elapsedMs));
		});

		if (filesToRead.length > 0) {
			void pass.completed.then(() => {
				logger.info(formatHashPassSummary(filesToRead.length, hashedBytes, performance.now() - startedAt));
			});
		}

		const hashByIdentity = new Map(filesToRead.map((file, index) => [fileIdentity(file), pass.hashes[index]]));
		return filesToHash.map((file) => {
			const identity = fileIdentity(file);
			const cached = sessionMemory.hashByFile.get(identity);
			return cached !== undefined ? Promise.resolve(cached) : hashByIdentity.get(identity)!;
		});
	};

	/**
	 * Uploads multiple files using the centralized task manager
	 * - All files appear in UI immediately (solves 0/10 display issue)
	 * - Concurrency is handled internally and transparently
	 * - Knowledge base updates are delayed until all files complete
	 */
	const uploadFileRecord = async (files: FileList | File[] | null, options?: TaskExecutionOptions) => {
		const { showNotification } = options || {};
		if (!files || files.length === 0) return;

		const { carried, toSchedule: uploadableFiles } = _selectFilesToSchedule(Array.from(files));

		// Announced whether or not anything is left to do, because a drop has to answer for itself
		// even when every file in it already has a row. Carrying both numbers is what lets the queue
		// answer about this drop rather than restating the last batch it happens to know about.
		signalDropLanded(carried.length, uploadableFiles.length);

		if (uploadableFiles.length === 0) return;

		try {
			const hashes = _resolveHashes(uploadableFiles);
			const taskDefinitions = uploadableFiles.map((file, index) => {
				const definition = _createFileTaskDefinition(file, hashes[index], options);
				sessionMemory.taskIdByFile.set(fileIdentity(file), definition.id);
				return definition;
			});

			// Schedule the entire batch with the task manager
			const batchOptions: BatchOptions = {
				showNotification,
				onComplete: options?.onBatchCompleted,
				onFailed: options?.onBatchFailed,
				onProgress: options?.onBatchProgress,
				progressEveryNthCompleted: 3,
			};

			await scheduleTaskBatch(taskDefinitions, batchOptions);
		} catch (error) {
			console.error('File upload preparation failed:', error);

			if (showNotification) {
				showNotification({
					description: 'Failed to prepare file upload.',
					type: 'error',
				});
			}
		}
	};

	/**
	 * Uploads a description (text content) using the centralized task manager
	 */
	const uploadTextRecord = async (text: string, options?: TaskExecutionOptions) => {
		if (!text) return;

		try {
			// Create task definition for text
			const taskDefinition = _createTextTaskDefinition(text, options);

			// Schedule as a single-item batch
			const batchOptions: BatchOptions = {
				onComplete: options?.onBatchCompleted,
				onFailed: options?.onBatchFailed,
				onProgress: options?.onBatchProgress,
				progressEveryNthCompleted: 3,
			};

			await scheduleTaskBatch([taskDefinition], batchOptions);
		} catch (error) {
			console.error('Description upload failed:', error);

			if (options?.showNotification) {
				options.showNotification({
					description: 'Failed to upload description.',
					type: 'error',
				});
			}
		}
	};

	/**
	 * Legacy compatibility: Get execution status from task manager
	 */
	const getExecutionStatus = () => {
		const status = getStatus();
		return {
			activeCount: status.activeTasks,
			queuedCount: status.queuedTasks,
			totalTasks: status.totalTasks,
			completedTasks: status.completedTasks,
			failedTasks: status.failedTasks,
			skippedTasks: status.skippedTasks,
			cancelledTasks: status.cancelledTasks,
		};
	};

	/**
	 * Legacy compatibility: Get concurrency status from task manager
	 */
	const getConcurrencyStatus = () => {
		const status = getStatus();
		return {
			active: status.activeTasks,
			queued: status.queuedTasks,
			totalLoad: status.totalLoad,
			canAcceptMore: status.canAcceptMore,
			totalPending: status.activeTasks + status.queuedTasks,
		};
	};

	return {
		uploadFileRecord,
		uploadTextRecord,
		getExecutionStatus,
		getConcurrencyStatus,
		// New task manager interface for components that want immediate UI feedback
		uiTasks,
		getTaskManagerStatus: getStatus,
	};
};
